import { useMemo } from "react";

import {
  CheckCircleIcon,
  SparklesIcon,
  StarIcon,
} from "@heroicons/react/24/outline";

import { CategoryWithParts } from "../../models/Part";
import classNames from "../../utils/classNames";
import {
  PRIORITY_COMPONENTS,
  PRIORITY_TIERS,
  PriorityTierId,
  PriorityTierMeta,
  getPriorityTier,
  isRecommendedTier,
  mockPriorityBreakdown,
  mockPriorityScore,
} from "../../utils/partPriority";

export interface CategoryWithPriority extends CategoryWithParts {
  /** Pre-computed mock score (0-100) so callers can re-use the value. */
  priorityScore: number;
  priorityTier: PriorityTierMeta;
}

interface PartPriorityQuickSelectProps {
  categories: CategoryWithParts[];
  selectedCategories: CategoryWithParts[];
  /**
   * Bulk-add every category whose tier is at or above the supplied
   * tier (rank: priority < high < medium < low < doNotRemove). Wired
   * through to the parent's `selectedByTableId` map so the assemblies
   * UI stays in sync.
   */
  onSelectTier: (
    tierId: PriorityTierId,
    matchingCategories: CategoryWithParts[]
  ) => void;
  /** Stable seed (e.g. vehicle id) for the mock-score derivation. */
  seedExtra?: string;
}

/**
 * Stylised infographic + quick-select bar that sits above the assemblies
 * picker on the Create Jobs page. Renders:
 *
 *  - A stacked progress bar showing how the categories on this vehicle
 *    distribute across the five priority tiers.
 *  - A clickable chip per tier (with count) that bulk-adds every
 *    category at or above that tier to the selected list — i.e. the
 *    "recommended parts list" the dismantler should be working from.
 *  - A "Recommended (Low+)" shortcut that picks every tier except
 *    "Do not remove".
 *
 * Mock-data only until the backend exposes a real `priorityScore`;
 * surfaces a "Demo data" pill so it's clear the infographic isn't
 * driven by live numbers.
 */
export default function PartPriorityQuickSelect({
  categories,
  selectedCategories,
  onSelectTier,
  seedExtra,
}: PartPriorityQuickSelectProps) {
  const enrichedCategories = useMemo<CategoryWithPriority[]>(() => {
    return categories.map((cat) => {
      const score = mockPriorityScore(cat.category.id, seedExtra);
      return {
        ...cat,
        priorityScore: score,
        priorityTier: getPriorityTier(score),
      };
    });
  }, [categories, seedExtra]);

  // Rarity is a vehicle-level metric so every category on this donor
  // shares the same value. Read it off the first category's breakdown.
  const vehicleRarity = useMemo(() => {
    if (categories.length === 0) return null;
    const breakdown = mockPriorityBreakdown(
      categories[0].category.id,
      seedExtra
    );
    const meta = PRIORITY_COMPONENTS.find((c) => c.key === "vehicleRarity");
    if (!meta) return null;
    return {
      score: breakdown.vehicleRarity.score,
      donorCount: breakdown.vehicleRarity.value,
      formatted: meta.formatValue(breakdown.vehicleRarity.value),
    };
  }, [categories, seedExtra]);

  const rarityTone = useMemo(() => {
    if (!vehicleRarity) return null;
    if (vehicleRarity.score >= 8) {
      return {
        label: "Rare vehicle",
        body:
          "Few of these donors come through — prioritise removing parts we don't already hold in stock.",
        chip: "bg-rose-50 text-rose-700 ring-rose-200",
        dot: "bg-rose-500",
      };
    }
    if (vehicleRarity.score >= 5) {
      return {
        label: "Uncommon vehicle",
        body:
          "Limited future opportunities — favour any parts we're short on.",
        chip: "bg-amber-50 text-amber-800 ring-amber-200",
        dot: "bg-amber-500",
      };
    }
    if (vehicleRarity.score >= 3) {
      return {
        label: "Common vehicle",
        body:
          "We see this regularly — only push parts where stock or demand justify it.",
        chip: "bg-sky-50 text-sky-800 ring-sky-200",
        dot: "bg-sky-500",
      };
    }
    return {
      label: "Very common vehicle",
      body:
        "Plenty of future donors — be selective, removing parts we already hold ties up storage.",
      chip: "bg-slate-100 text-slate-700 ring-slate-200",
      dot: "bg-slate-400",
    };
  }, [vehicleRarity]);

  const totalsByTier = useMemo(() => {
    const counts: Record<PriorityTierId, number> = {
      priority: 0,
      high: 0,
      medium: 0,
      low: 0,
      doNotRemove: 0,
    };
    enrichedCategories.forEach((cat) => {
      counts[cat.priorityTier.id] += 1;
    });
    return counts;
  }, [enrichedCategories]);

  const selectedTierIds = useMemo(() => {
    const set = new Set<PriorityTierId>();
    selectedCategories.forEach((sel) => {
      const score = mockPriorityScore(sel.category.id, seedExtra);
      set.add(getPriorityTier(score).id);
    });
    return set;
  }, [selectedCategories, seedExtra]);

  const total = enrichedCategories.length;
  const recommendedCount = enrichedCategories.filter((c) =>
    isRecommendedTier(c.priorityTier)
  ).length;

  const selectedRecommendedCount = useMemo(() => {
    const ids = new Set(
      enrichedCategories
        .filter((c) => isRecommendedTier(c.priorityTier))
        .map((c) => c.category.id)
    );
    return selectedCategories.filter((s) => ids.has(s.category.id)).length;
  }, [enrichedCategories, selectedCategories]);

  const handleTierClick = (tier: PriorityTierMeta) => {
    // "At or above" — anything with rank <= the clicked tier's rank.
    const matching = enrichedCategories.filter(
      (c) => c.priorityTier.rank <= tier.rank
    );
    onSelectTier(tier.id, matching);
  };

  const handleRecommendedClick = () => {
    const matching = enrichedCategories.filter((c) =>
      isRecommendedTier(c.priorityTier)
    );
    // Use the lowest recommended tier id as the "trigger" so callers can
    // colour-match a notification etc. if they want.
    onSelectTier("low", matching);
  };

  if (total === 0) return null;

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-1.5 text-base font-semibold text-slate-900">
            <StarIcon
              className="h-4 w-4 text-amber-500"
              aria-hidden="true"
            />
            Recommended parts
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Each category is scored 0-100 — quick-select a priority tier to
            bulk-add everything above it to this job.
          </p>
        </div>
        <span
          className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700"
          title="Placeholder priority scores until the backend ships its recommendation engine"
        >
          <SparklesIcon className="h-3 w-3" aria-hidden="true" />
          Demo data
        </span>
      </div>

      {vehicleRarity && rarityTone && (
        <div
          className={classNames(
            "flex items-start gap-2 rounded-lg px-3 py-2 ring-1 ring-inset",
            rarityTone.chip
          )}
        >
          <span
            className={classNames(
              "mt-0.5 inline-block h-2 w-2 shrink-0 rounded-full",
              rarityTone.dot
            )}
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1 text-xs">
            <p className="flex flex-wrap items-baseline gap-x-1.5 font-semibold">
              {rarityTone.label}
              <span className="text-[11px] font-normal opacity-80">
                · {vehicleRarity.formatted}
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
                Rarity {vehicleRarity.score}/10
              </span>
            </p>
            <p className="mt-0.5 text-[11px] leading-snug opacity-90">
              {rarityTone.body}
            </p>
          </div>
        </div>
      )}

      {/* Stacked distribution bar. Width per segment is proportional to
          its share of the total category count, so a vehicle with mostly
          medium-priority parts reads as such at a glance. */}
      <div className="space-y-1.5">
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100 ring-1 ring-inset ring-slate-200">
          {PRIORITY_TIERS.map((tier) => {
            const count = totalsByTier[tier.id];
            if (count === 0) return null;
            const widthPct = (count / total) * 100;
            return (
              <span
                key={tier.id}
                className={classNames(
                  "h-full transition-all",
                  tier.segmentClass
                )}
                style={{ width: `${widthPct}%` }}
                title={`${tier.label}: ${count} categor${count === 1 ? "y" : "ies"}`}
              />
            );
          })}
        </div>
        <div className="flex items-center justify-between text-[11px] text-slate-500">
          <span>
            <span className="font-semibold text-slate-700">
              {recommendedCount}
            </span>{" "}
            recommended of {total} total
          </span>
          <span>
            {selectedRecommendedCount > 0 && (
              <>
                <span className="font-semibold text-emerald-700">
                  {selectedRecommendedCount}
                </span>{" "}
                selected
              </>
            )}
          </span>
        </div>
      </div>

      {/* Tier chips — clicking one bulk-adds every category at or above
          that tier. The chip styling escalates from the recommendation-
          floor (Low) through to the must-remove top tier (Priority). */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleRecommendedClick}
          disabled={recommendedCount === 0}
          className={classNames(
            "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40",
            recommendedCount === 0
              ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
              : "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
          )}
          title={
            recommendedCount === 0
              ? "No categories meet the recommended threshold"
              : `Add all ${recommendedCount} recommended categories (Low and above)`
          }
        >
          <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
          Recommended ({recommendedCount})
        </button>

        <span className="hidden h-5 w-px bg-slate-200 sm:inline-block" />

        {PRIORITY_TIERS.map((tier) => {
          const count = totalsByTier[tier.id];
          const isSelected = selectedTierIds.has(tier.id);
          const disabled = count === 0;

          return (
            <button
              key={tier.id}
              type="button"
              disabled={disabled}
              onClick={() => handleTierClick(tier)}
              className={classNames(
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40",
                disabled
                  ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                  : isSelected
                    ? tier.buttonSelectedClass
                    : tier.buttonIdleClass
              )}
              title={
                disabled
                  ? `No categories in ${tier.label}`
                  : tier.id === "doNotRemove"
                    ? `Add every category (including ${count} we'd usually skip)`
                    : `Add all categories at ${tier.label} or above`
              }
            >
              <span
                className={classNames(
                  "inline-block h-2 w-2 rounded-full",
                  tier.segmentClass
                )}
                aria-hidden="true"
              />
              {tier.shortLabel}
              <span
                className={classNames(
                  "rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums",
                  disabled
                    ? "bg-slate-100 text-slate-400"
                    : isSelected
                      ? "bg-white/20 text-white"
                      : "bg-white/70 text-slate-700"
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <p className="text-[11px] text-slate-400">
        Score bands · Priority 86-100 · High 71-85 · Medium 56-70 · Low
        41-55 · Do not remove 0-40
      </p>
    </section>
  );
}
